import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import Popper from 'popper.js';

export interface Option {
    disable: boolean;
    label: string;
    value: string;
}

export interface GroupedOptions {
    [groupName: string]: Option[];
}

type InputProps = {
    options: GroupedOptions;
    selected: string;
    setSelected: (selected: string) => void;
    minWidth?: string;
    maxHeight?: string;
    textColor?: string;
    disabled?: boolean;
    hasErrorBorder?: boolean;
    placeholder?: string;
    isButton?: boolean;
    searchPlaceholder?: string;
    emptySearchMessage?: string;
    className?: string;
    groupTextColor?: 'gray' | 'marron';
};

export function GroupedSearchDropdown({
    options,
    selected,
    setSelected,
    minWidth = "14rem",
    maxHeight = "300px",
    textColor = "white",
    disabled = false,
    hasErrorBorder = false,
    placeholder = 'Select an option',
    isButton = false,
    searchPlaceholder = 'Search...',
    emptySearchMessage = 'No options found',
    className = '',
    groupTextColor = 'gray',
}: InputProps) {
    const [dropdownPopoverShow, setDropdownPopoverShow] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const btnDropdownRef = useRef<HTMLButtonElement>(null);
    const popoverDropdownRef = useRef<HTMLDivElement>(null);
    const nodeRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    
    // Filter options based on search query
    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) {
            return options;
        }

        const filtered: GroupedOptions = {};
        const query = searchQuery.toLowerCase();

        Object.entries(options).forEach(([groupName, groupOptions]) => {
            const filteredGroupOptions = groupOptions.filter(option =>
                option.label.toLowerCase().includes(query)
            );
            
            if (filteredGroupOptions.length > 0) {
                filtered[groupName] = filteredGroupOptions;
            }
        });

        return filtered;
    }, [options, searchQuery]);

    // Get selected option label
    const selectedLabel = useMemo(() => {
        for (const groupOptions of Object.values(options)) {
            const found = groupOptions.find(opt => opt.value === selected);
            if (found) return found.label;
        }
        return placeholder;
    }, [options, selected, placeholder]);

    const handleClickOutside = (e: Event) => {
        if (nodeRef.current?.contains(e.target as Node)) {
            return;
        }
        setDropdownPopoverShow(false);
    };

    const openDropdownPopover = () => {
        new Popper(btnDropdownRef.current!, popoverDropdownRef.current!, {
            placement: "bottom-start"
        });
        setDropdownPopoverShow(true);
        // Focus search input when dropdown opens
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    const closeDropdownPopover = () => {
        setDropdownPopoverShow(false);
        setSearchQuery('');
    };

    const handleSelect = (option: Option) => {
        if (option.disable) {
            return;
        }
        setSelected(option.value);
        closeDropdownPopover();
    };

    useEffect(() => {
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownPopoverShow]);

    const hasFilteredOptions = Object.keys(filteredOptions).length > 0;

    return (
        <div ref={nodeRef} className={`flex flex-wrap ${className}`} style={{ minWidth }}>
            <div className={`flex w-full ${isButton ? 'portal-provider-fat-green-btn rounded-full' : ''}`}>
                <div
                    className={`${disabled ? "bg-gray-100 opacity-50 pointer-events-none" : ""} ${hasErrorBorder ? "border-red-500" : ""} relative inline-flex align-middle w-full rounded border border-${textColor} ${isButton ? 'rounded-full' : ''}`}
                >
                    <button
                        className={`text-${textColor} w-full font-bold text-left text-sm pl-4 pr-8 py-2 outline-none focus:outline-none bg-transparent`}
                        style={{ transition: "all .15s ease" }}
                        type="button"
                        ref={btnDropdownRef}
                        onClick={() => {
                            if (disabled) return;
                            dropdownPopoverShow
                                ? closeDropdownPopover()
                                : openDropdownPopover();
                        }}
                    >
                        {selectedLabel}
                    </button>
                    
                    <div
                        ref={popoverDropdownRef}
                        className={`${
                            dropdownPopoverShow ? "block" : "hidden"
                        } ${
                            disabled ? "opacity-50" : ""
                        } bg-white text-base z-50 float-left py-2 list-none text-left shadow-lg mt-1 overflow-hidden`}
                        style={{ minWidth, maxHeight }}
                    >
                        {/* Search Input */}
                        <div className="px-3 pb-2 sticky top-0 bg-white z-10 border-b border-gray-200">
                            <div className="relative flex items-center">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={searchPlaceholder}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`w-full py-2 pl-3 pr-3 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900 placeholder-gray-400`}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>

                        {/* Options */}
                        <div className="overflow-y-auto" style={{ maxHeight: `calc(${maxHeight} - 60px)` }}>
                            {!hasFilteredOptions ? (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {emptySearchMessage}
                                </div>
                            ) : (
                                Object.entries(filteredOptions).map(([groupName, groupOptions]) => (
                                    <div key={groupName}>
                                        {/* Group Header */}
                                        <div className={`px-4 py-2 text-xs font-semibold ${groupTextColor === 'gray' ? 'text-gray-600' : 'text-red-marron'} uppercase tracking-wider bg-gray-50`}>
                                            {groupName}
                                        </div>
                                        
                                        {/* Group Options */}
                                        {groupOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                className={`text-sm py-2 px-4 font-normal block w-full text-left whitespace-no-wrap hover:bg-botticelli-100 hover:text-white transition-colors ${
                                                    selected === option.value
                                                        ? 'bg-botticelli-100 text-white'
                                                        : option.disable
                                                        ? 'text-gray-400 cursor-not-allowed'
                                                        : 'bg-white text-gray-800'
                                                }`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleSelect(option);
                                                }}
                                                disabled={option.disable}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <MdArrowDropDown className="-ml-8 mt-1" size={32} />
            </div>
        </div>
    );
}
